import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from "../supabase";
import { useAuth } from "../context/AuthContext";
import { buildBrandOptions, normalizeBrandKey, normalizeBrandLabel } from '../utils/brandFilters';
import {
  Package, Search, Plus,
  Layers, Barcode, Tag,
  ChevronRight, ChevronDown, ChevronUp,
  Filter,
  Box,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { buildProductStatusPatch, normalizeProductStatus } from '../utils/productStatus';
import { getCatalogSalePrice, getCatalogUnitCost } from '../utils/catalogPricing';
import { getCleanVariantLabel, getVariantName } from '../utils/productLabels';
import {
  fetchAllPaginated,
  getProductStockForSite,
  hydrateLegacyVariantStock,
  resolvePreferredStockSiteId,
} from '../utils/productCatalog';

// --- FIX IMÁGENES: Función ROBUSTA para reconstruir la URL ---
const resolveImageUrl = (path) => {
    if (!path) return null;
    const rawPath = path.trim();
    if (rawPath.startsWith('blob:')) return null; // Ignorar URLs temporales rotas
    if (rawPath.includes('http') || rawPath.includes('base64')) return rawPath;
    if (rawPath.startsWith('/img/') || rawPath.startsWith('img/')) {
      return rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
    }
    if (rawPath.startsWith('/storage/')) {
      return `https://ltpbzblgasmfvcagtlti.supabase.co${rawPath}`;
    }

    const PROJECT_URL = "https://ltpbzblgasmfvcagtlti.supabase.co";
    const BUCKET_NAME = "products";
    const cleanPath = rawPath.replace(/^\/+/, '');
    return `${PROJECT_URL}/storage/v1/object/public/${BUCKET_NAME}/${cleanPath}`;
};

const Productos = ({ onNavigate, sitioId: sitioActivo }) => {
  const { profile, organization } = useAuth();
  const navigate = useNavigate();

  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usdRate, setUsdRate] = useState(0);

  // Org settings
  const [enableVariants, setEnableVariants] = useState(false);

  // Filtros
  const [busqueda, setBusqueda] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("todas");
  const [filtroMarca, setFiltroMarca] = useState("todas");
  const [filtroClasificacion, setFiltroClasificacion] = useState("todas");
  const [filtroEstado, setFiltroEstado] = useState("activos"); // "activos" | "inactivos" | "todos" | "archivados"
  const [filtroSitio, setFiltroSitio] = useState(sitioActivo || "todos");

  // Estado para animacion de confirmacion al togglear activo/inactivo
  const [confirmPulse, setConfirmPulse] = useState({});
  const [togglingIds, setTogglingIds] = useState({});

  // Tree expansion (only when enableVariants)
  const [expandedProducts, setExpandedProducts] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  // Observability
  const [perfStart, setPerfStart] = useState(null);
  const perfStartRef = useRef(null);

  // âœ… Navegación robusta (prioriza onNavigate del Sistema, y si no existe cae a react-router)
  const go = (module, entityId = null) => {
    try {
      if (typeof onNavigate === 'function') return onNavigate(module, entityId);
      if (entityId) return navigate(`/sistema/${module}/${entityId}`);
      return navigate(`/sistema/${module}`);
    } catch (e) {
      console.error('Error navegando:', e);
      toast.error('No se pudo navegar. Revisá Sistema.jsx / rutas.');
    }
  };

  useEffect(() => {
    if (profile?.organization_id) {
      fetchData();
      logPerformanceStart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  // ✅ FIX colateral multi-sucursal: el filtro de sitio ahora reacciona al cambio
  // del Topbar. Si el padre Sistema.jsx pasa un `sitioActivo` (derivado del
  // SiteContext) lo aplicamos en cada cambio. Si va a "Consolidado" (sitioActivo
  // null), volvemos a mostrar todos los sitios.
  useEffect(() => {
    setFiltroSitio(sitioActivo || 'todos');
  }, [sitioActivo]);

  const logUsage = async (button_clicked) => {
    try {
      if (!profile?.organization_id) return;

      await supabase.from('usage_logs').insert([{
        organization_id: profile.organization_id,
        user_id: profile?.id || null,
        user_role: profile?.role || null,
        module_id: 'productos',
        button_clicked,
        time_spent_seconds: 0
      }]);
    } catch (e) { /* telemetria best-effort */ }
  };

  const logPerformanceStart = async () => {
    try {
      const now = Date.now();
      perfStartRef.current = now;
      setPerfStart(now);
    } catch (e) { /* telemetria best-effort */ }
  };

  const logPerformanceEnd = async (action = 'view_module') => {
    try {
      const start = perfStartRef.current || perfStart;
      if (!profile?.organization_id || !start) return;
      const duration = Math.max(0, Math.round((Date.now() - start) / 1000));

      await supabase.from('performance_logs').insert([{
        user_id: profile?.id || null,
        organization_id: profile.organization_id,
        module_id: 'productos',
        duration_seconds: duration,
        action
      }]);
    } catch (e) { /* telemetria best-effort */ }
    finally {
      perfStartRef.current = null;
      setPerfStart(null);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const orgId = profile.organization_id;

      // Org settings: vienen del AuthContext (un fetch por sesión).
      const org = organization;
      const flag = !!org?.enable_variants;
      setEnableVariants(flag);
      setUsdRate(Number(org?.usd_exchange_rate || 0));

      const { data: categoriasData, error: categoriasError } = await supabase
        .from('product_categories')
        .select('*')
        .eq('organization_id', orgId)
        .order('name', { ascending: true });
      if (categoriasError) throw categoriasError;

      const { data: sitiosData, error: sitiosError } = await supabase
        .from('sitios')
        .select('*')
        .eq('organization_id', orgId)
        .order('nombre', { ascending: true });
      if (sitiosError) throw sitiosError;

      const defaultSiteId = resolvePreferredStockSiteId(org, sitiosData || []);
      const legacyStockResult = await hydrateLegacyVariantStock({
        supabase,
        organizationId: orgId,
        defaultSiteId,
      });

      // Cargamos activos, inactivos y archivados. Todo el filtro por estado se
      // aplica client-side para que el usuario pueda alternar sin recargar.
      // Los archivados (eliminados con historial, ver utils/productDeletion.js)
      // no aparecen en ninguna vista operativa, pero siguen alcanzables desde el
      // filtro "Archivados": excluirlos de la query los volvería invisibles y no
      // hay ninguna otra pantalla para verlos.
      const productosData = await fetchAllPaginated(() =>
        supabase
          .from('products')
          .select(`*,
            product_categories(name),
            stock_levels(*),
            product_variants(id, price, sku, barcode, attributes, stock_quantity, talle, color_id, activo, archivado_at)
          `)
          .eq('organization_id', orgId)
          .order('name', { ascending: true })
      );

      setProductos((productosData || []).map(normalizeProductStatus));
      setCategorias(categoriasData || []);
      setSitios(sitiosData || []);

      if (legacyStockResult.inserted > 0) {
        toast.success(`Se recuperó el stock importado en ${legacyStockResult.inserted} productos.`);
      }

      logPerformanceEnd('load_module');
    } catch (error) {
      console.error("Error al cargar productos:", error);
      toast.error("No se pudieron cargar los productos.");
    } finally {
      setLoading(false);
    }
  };

  const getStock = (prod) => {
    const siteId = !filtroSitio || filtroSitio === "todos" ? null : filtroSitio;
    return getProductStockForSite(prod, siteId);
  };

  const fmtMoney = (n) => {
    const v = Number(n || 0);
    return v.toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
  };

  // âœ… Fuente de verdad para el listado: variants
  const getVariants = (prod) => (Array.isArray(prod?.product_variants) ? prod.product_variants : []);
  const getSalePrice = (prod) => {
    return getCatalogSalePrice(prod, usdRate);
  };
  const getUnitCost = (prod) => getCatalogUnitCost(prod);

  const brandOptions = useMemo(() => buildBrandOptions(productos, (prod) => prod?.brand), [productos]);
  const categoriaMap = useMemo(
    () => new Map(categorias.map((categoria) => [categoria.id, categoria.name])),
    [categorias]
  );

  const openDetalle = (prodId) => {
    try {
      logUsage('abrir_producto_detalle');
      go('producto-detalle', prodId);
    } catch (e) {
      console.error(e);
      toast.error("No se pudo abrir el detalle. Revisá Sistema.jsx / onNavigate.");
    }
  };

  // --- Estado activo/inactivo ---
  const isEffectivelyActive = (prod) => Boolean(normalizeProductStatus(prod)?.active);

  const toggleProductoActivo = async (prod) => {
    if (!prod?.id) return;
    if (togglingIds[prod.id]) return; // evita doble click

    const nextActive = !isEffectivelyActive(prod);

    // Optimistic update local
    setTogglingIds((curr) => ({ ...curr, [prod.id]: true }));
    setProductos((curr) =>
      curr.map((p) =>
        p.id === prod.id ? { ...p, active: nextActive, is_active: nextActive } : p,
      ),
    );

    try {
      const { error } = await supabase
        .from('products')
        .update(buildProductStatusPatch(nextActive))
        .eq('id', prod.id);

      if (error) throw error;

      // Pulso de confirmacion 1s
      setConfirmPulse((curr) => ({ ...curr, [`toggle-${prod.id}`]: true }));
      setTimeout(() => {
        setConfirmPulse((curr) => {
          const next = { ...curr };
          delete next[`toggle-${prod.id}`];
          return next;
        });
      }, 1000);

      toast.success(nextActive ? 'Producto activado' : 'Producto desactivado');
    } catch (err) {
      console.error(err);
      // Revertir
      setProductos((curr) =>
        curr.map((p) =>
          p.id === prod.id ? { ...p, active: !nextActive, is_active: !nextActive } : p,
        ),
      );
      toast.error('No se pudo cambiar el estado del producto');
    } finally {
      setTogglingIds((curr) => {
        const next = { ...curr };
        delete next[prod.id];
        return next;
      });
    }
  };

  // Variants helpers (tree labels)
  const variantBadge = (v) => {
    const attrs = (v?.attributes && typeof v.attributes === 'object') ? v.attributes : {};
    const parts = [];
    // Avoid showing internal variant_description in badge
    Object.entries(attrs).forEach(([k, val]) => {
      if (!k) return;
      if (k === 'variant_description') return;
      if (val === null || val === undefined || String(val).trim() === '') return;
      parts.push(`${k}: ${String(val)}`);
    });
    const attrLabel = parts.slice(0, 2).join(' Â· ');
    const sku = (v?.sku || '').trim();
    const bc = (v?.barcode || '').trim();
    const head = sku || bc || 'VAR';
    return attrLabel ? `${head} â€” ${attrLabel}` : head;
  };

  // Filtering
  const filtrados = useMemo(() => {
    const b = busqueda.trim().toLowerCase();

    const baseList = (productos || []).filter((p) => {
      const matchBusqueda =
        !b ||
        (p.name || "").toLowerCase().includes(b) ||
        normalizeBrandLabel(p.brand).toLowerCase().includes(b) ||
        (p.barcode || "").toLowerCase().includes(b) ||
        // Talles y colores: encontrar el artículo escaneando/pegando el
        // código o SKU de cualquiera de sus variantes activas.
        (p.stock_tracking === 'variant' && (p.product_variants || []).some((v) =>
          !v?.archivado_at && v?.activo !== false &&
          (String(v?.sku || '').toLowerCase().includes(b) || String(v?.barcode || '').toLowerCase().includes(b))));

      const catId = p.category_id || null;
      const matchCategoria =
        filtroCategoria === "todas" ||
        (filtroCategoria === "sin" && !catId) ||
        (catId === filtroCategoria);

      const tipo = (p.product_type || "Producto Terminado").toLowerCase();
      const matchMarca = filtroMarca === "todas" || normalizeBrandKey(p.brand) === filtroMarca;
      // âœ… ACTUALIZADO: La lógica de filtro ahora agrupa "insumo" y "materia" para mostrar Materias Primas correctamente
      const matchClasificacion =
        filtroClasificacion === "todas" ||
        (filtroClasificacion === "terminado" && tipo.includes("terminado")) ||
        (filtroClasificacion === "insumo" && (tipo.includes("insumo") || tipo.includes("materia")));

      // Filtro por estado. El archivado es un estado aparte: un producto
      // eliminado no debe aparecer ni en "activos", ni en "inactivos", ni en
      // "todos" — solo si se lo pide explícitamente.
      const activo = isEffectivelyActive(p);
      const archivado = Boolean(p.archived_at);
      const matchEstado =
        filtroEstado === "archivados"
          ? archivado
          : !archivado &&
            (filtroEstado === "todos" ||
              (filtroEstado === "activos" && activo) ||
              (filtroEstado === "inactivos" && !activo));

      return matchBusqueda && matchCategoria && matchMarca && matchClasificacion && matchEstado;
    });

    return baseList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productos, busqueda, filtroCategoria, filtroMarca, filtroClasificacion, filtroEstado, filtroSitio, enableVariants]);

  // --- KPI ---
  // Los KPIs cuentan solo activos (es la base operativa del catalogo).
  const kpis = useMemo(() => {
    const list = (Array.isArray(productos) ? productos : []).filter((p) => isEffectivelyActive(p));
    const total = list.length;
    const conStock = list.filter((p) => getStock(p) > 0).length;
    const stockBajo = list.filter((p) => {
      const stock = getStock(p);
      const min = Number(p?.min_stock || 0);
      return stock > 0 && min > 0 && stock <= min;
    }).length;
    const sinPrecio = list.filter(p => (Number(getSalePrice(p)) || 0) <= 0).length;
    return { total, conStock, stockBajo, sinPrecio };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productos, filtroSitio]);

  const toggleExpand = (productId) => {
    setExpandedProducts((p) => ({ ...p, [productId]: !p[productId] }));
  };

  const shouldShowTree = (prod) => enableVariants && getVariants(prod).length > 1;
  const getCategoryName = (prod) =>
    prod.product_categories?.name || categoriaMap.get(prod.category_id) || prod.category || 'Sin categoría';

  const sortedFiltrados = useMemo(() => {
    const arr = [...filtrados];
    const { key, direction } = sortConfig;

    const getSortValue = (prod) => {
      switch (key) {
        case 'name':
          return prod.name || '';
        case 'category':
          return getCategoryName(prod);
        case 'brand':
          return normalizeBrandLabel(prod.brand);
        case 'barcode':
          return prod.barcode || '';
        case 'stock':
          return Number(getStock(prod) || 0);
        case 'cost':
          return Number(getUnitCost(prod) || 0);
        case 'price':
          return Number(getSalePrice(prod) || 0);
        default:
          return '';
      }
    };

    arr.sort((a, b) => {
      const av = getSortValue(a);
      const bv = getSortValue(b);

      if (typeof av === 'string' || typeof bv === 'string') {
        const as = String(av ?? '').toLowerCase();
        const bs = String(bv ?? '').toLowerCase();
        return direction === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
      }

      const an = Number(av || 0);
      const bn = Number(bv || 0);
      return direction === 'asc' ? an - bn : bn - an;
    });

    return arr;
  }, [filtrados, sortConfig, categoriaMap, filtroSitio, usdRate]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }

      const defaultDescKeys = ['stock', 'cost', 'price'];
      return { key, direction: defaultDescKeys.includes(key) ? 'desc' : 'asc' };
    });
  };

  const SortableTh = ({ label, sortKey, align = 'left', className = '' }) => {
    const isActive = sortConfig.key === sortKey;

    return (
      <th
        className={`group px-4 py-4 cursor-pointer hover:bg-slate-100 transition-colors select-none ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} ${className}`}
        onClick={() => handleSort(sortKey)}
      >
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start'}`}>
          <div className="leading-tight text-inherit">{label}</div>
          <div className="flex flex-col">
            {isActive && sortConfig.direction === 'asc' && <ChevronUp size={10} className="text-[#51A7FD]" />}
            {isActive && sortConfig.direction === 'desc' && <ChevronDown size={10} className="text-[#51A7FD]" />}
            {!isActive && <div className="h-2.5 w-2.5 opacity-0 group-hover:opacity-30"><Filter size={10} /></div>}
          </div>
        </div>
      </th>
    );
  };

  if (loading) {
    return (
      <div className="imp-page imp-container">
        <div className="h-full flex flex-col items-center justify-center p-8">
          <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
            <Package className="text-slate-300 animate-pulse" size={18} />
          </div>
          <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Cargando Productos...</p>
        </div>
      </div>
    );
  }

  return (
    // âœ… Responsive macro: contenedor + padding consistente en todas las pantallas
    <div className="imp-page imp-container animate-nova-in">
      {/* HEADER (sin Volver / sin Refrescar) */}
      <div className="imp-header mb-4 gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Package size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Productos</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Catálogo, stock y precios.
            </p>
          </div>
        </div>

        {/* âœ… ACCIONES: wrap natural en tablet / mobile */}
        <div className="imp-header-actions w-full lg:w-auto">
          <button
            onClick={() => { logUsage('ir_combos'); go('combos'); }}
            className="w-full sm:flex-1 lg:w-auto lg:flex-none px-5 py-2.5 rounded-2xl bg-[#51A7FD] text-white shadow-lg hover:shadow-xl hover:opacity-95 transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
            title="Gestionar combos"
          >
            <Layers size={16} /> Combos
          </button>

          <button
            data-onboarding-id="productos-new-button"
            onClick={() => { logUsage('nuevo_producto'); go('producto-nuevo'); }}
            className="w-full sm:flex-1 lg:w-auto lg:flex-none px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-lg hover:shadow-xl transition-all text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
          >
            <Plus size={16} /> Nuevo producto
          </button>
        </div>
      </div>

      {/* KPI (clickable) â€” âœ… grilla responsive consistente (modelo productos) */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Total</p>
            <Package size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">En catálogo</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Con stock</p>
            <Box size={16} className="text-emerald-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{kpis.conStock}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Disponibles</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Stock bajo</p>
            <Layers size={16} className="text-amber-300" />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${kpis.stockBajo > 0 ? 'text-amber-700' : 'text-slate-800'}`}>{kpis.stockBajo}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Reposición</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Sin precio</p>
            <Barcode size={16} className="text-slate-300" />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${kpis.sinPrecio > 0 ? 'text-rose-700' : 'text-slate-800'}`}>{kpis.sinPrecio}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Revisar carga</p>
        </div>
      </div>


      {/* FILTROS (gap reducido) */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 md:p-4 mb-3">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2.5">
          {/* BUSCADOR */}
          <div className="lg:col-span-4">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Search size={12} /> Buscar
            </label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pl-11 pr-3 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-[11px] font-bold outline-none focus:ring-2 focus:ring-[#51A7FD]/10 focus:border-[#51A7FD] shadow-inner uppercase"
                data-onboarding-id="productos-search-input"
                placeholder="BUSCAR POR NOMBRE O CÓDIGO..."
              />
            </div>
          </div>

          {/* CATEGORÍA */}
          <div className="lg:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Tag size={12} /> Categoría
            </label>
            <select
              value={filtroCategoria}
              onChange={(e) => setFiltroCategoria(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="todas">Todas</option>
              <option value="sin">Sin categoría</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* MARCA */}
          <div className="lg:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Tag size={12} /> Marca
            </label>
            <select
              value={filtroMarca}
              onChange={(e) => setFiltroMarca(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="todas">Todas</option>
              {brandOptions.map((brand) => (
                <option key={brand.value} value={brand.value}>{brand.label}</option>
              ))}
            </select>
          </div>

          {/* CLASIFICACIÓN */}
          <div className="lg:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Layers size={12} /> Tipo
            </label>
            <select
              value={filtroClasificacion}
              onChange={(e) => setFiltroClasificacion(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="todas">Todas</option>
              <option value="terminado">Terminado</option>
              <option value="insumo">Insumo / Materia Prima</option>
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-1">
              <Package size={12} /> Estado
            </label>
            <select
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
              className="mt-1 w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all cursor-pointer shadow-inner"
            >
              <option value="activos">Activos</option>
              <option value="inactivos">Inactivos</option>
              <option value="todos">Todos</option>
              <option value="archivados">Archivados (eliminados)</option>
            </select>
          </div>
        </div>

        {/* SITIO */}
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
            <span className="inline-flex items-center gap-2">
              <ChevronRight size={14} className="text-slate-300" />
              Sucursal / Sitio
            </span>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
            <button
              onClick={() => setFiltroSitio("todos")}
              className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                filtroSitio === "todos"
                  ? 'bg-[#0B2A36] text-white shadow-md'
                  : 'bg-slate-50 text-slate-400 border border-slate-100 hover:text-slate-600'
              }`}
            >
              Todos
            </button>

            {sitios.map((s) => (
              <button
                key={s.id}
                onClick={() => setFiltroSitio(s.id)}
                className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest whitespace-nowrap transition-all ${
                  filtroSitio === s.id
                    ? 'bg-[#0B2A36] text-white shadow-md'
                    : 'bg-slate-50 text-slate-400 border border-slate-100 hover:text-slate-600'
              }`}
              >
                {s.nombre}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* LISTADO */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Package size={16} className="text-[#3FF2AA]" />
            <span className="text-[10px] font-black text-white uppercase tracking-widest">
              Listado ({sortedFiltrados.length})
            </span>
          </div>
          <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">
            {enableVariants ? 'Variantes activas' : 'Variantes desactivadas'}
          </div>
        </div>

        {/* Tarjetas compactas para mobile */}
        <div className="lg:hidden divide-y divide-slate-100">
          {sortedFiltrados.length === 0 ? (
            <div className="py-14 text-center text-slate-300 text-[10px] font-black uppercase tracking-[0.28em]">
              Sin resultados
            </div>
          ) : (
            sortedFiltrados.map((prod) => {
              const stock = getStock(prod);
              const minStock = Number(prod?.min_stock || 0);
              const catName = getCategoryName(prod);
              const brandName = normalizeBrandLabel(prod.brand);
              const price = getSalePrice(prod);
              const cost = getUnitCost(prod);
              const variants = getVariants(prod);
              const hasTree = shouldShowTree(prod);
              const tipoStr = (prod.product_type || 'Producto Terminado').toLowerCase();
              const esInsumo = tipoStr.includes('insumo') || tipoStr.includes('materia');
              const safeImageUrl = resolveImageUrl(prod.image_url);
              const stockTone = stock <= 0
                ? 'bg-rose-50 text-rose-700 border-rose-100'
                : stock <= minStock && minStock > 0
                  ? 'bg-amber-50 text-amber-700 border-amber-100'
                  : 'bg-emerald-50 text-emerald-700 border-emerald-100';

              return (
                <button
                  key={`mobile-${prod.id}`}
                  type="button"
                  onClick={() => openDetalle(prod.id)}
                  className="w-full text-left p-4 bg-white active:bg-slate-50 transition-colors"
                  title="Abrir detalle"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-200 shadow-sm flex items-center justify-center shrink-0 overflow-hidden">
                      {safeImageUrl ? (
                        <img src={safeImageUrl} alt={prod.name} className="w-full h-full object-cover" />
                      ) : (
                        <Package size={18} className="text-slate-300" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-[11px] font-black uppercase tracking-tight text-slate-800 break-words">
                            {prod.name}
                          </p>
                          <p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-slate-400 break-words">
                            {brandName} Â· {catName}
                          </p>
                        </div>
                        <ChevronRight size={16} className="text-slate-300 shrink-0" />
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl border text-[9px] font-black uppercase tracking-widest ${
                          esInsumo
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }`}>
                          {esInsumo ? <Layers size={10} /> : <Box size={10} />}
                          {prod.product_type || 'Producto Terminado'}
                        </span>
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-slate-50 border border-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-500">
                          <Barcode size={12} className="text-slate-300" />
                          {prod.barcode || 'SIN EAN'}
                        </span>
                        {hasTree ? (
                          <span className="inline-flex px-3 py-1 rounded-xl bg-slate-50 border border-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-500">
                            {variants.length} variantes
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Stock</p>
                          <span className={`mt-1 inline-flex px-2.5 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest border ${stockTone}`}>
                            {stock}
                          </span>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-right">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Costo</p>
                          <p className={`mt-1 text-[11px] font-black uppercase tracking-tight ${cost > 0 ? 'text-slate-700' : 'text-amber-600'}`}>
                            {cost > 0 ? fmtMoney(cost) : 'SIN COSTO'}
                          </p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2 text-right">
                          <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Precio</p>
                          <p className={`mt-1 text-[11px] font-black uppercase tracking-tight ${price > 0 ? 'text-slate-700' : 'text-rose-600'}`}>
                            {price > 0 ? fmtMoney(price) : 'SIN PRECIO'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div data-onboarding-id="productos-list" className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[1120px] text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                <SortableTh label="Producto" sortKey="name" className="px-5" />
                <SortableTh label="Categoría" sortKey="category" />
                <SortableTh label="Marca" sortKey="brand" />
                <SortableTh label="Código de barra" sortKey="barcode" />
                <SortableTh label="Stock" sortKey="stock" align="center" />
                <SortableTh label="Costo unitario" sortKey="cost" align="right" />
                <SortableTh label="Precio venta" sortKey="price" align="right" />
                <th className="px-5 py-4 w-10"></th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-50">
              {sortedFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-14 text-center text-slate-300 text-[10px] font-black uppercase tracking-[0.28em]">
                    Sin resultados
                  </td>
                </tr>
              ) : (
                sortedFiltrados.map((prod) => {
                  const stock = getStock(prod);
                  const brandName = normalizeBrandLabel(prod.brand);
                  const price = getSalePrice(prod);
                  const cost = getUnitCost(prod);
                  const safeImageUrl = resolveImageUrl(prod.image_url);
                  const catName = getCategoryName(prod);
                  const stockTone = stock <= 0
                    ? 'bg-rose-50 text-rose-700 border-rose-100'
                    : 'bg-emerald-50 text-emerald-700 border-emerald-100';
                  const variants = getVariants(prod);
                  const meaningfulVariants = variants.filter((v) =>
                    Boolean(getCleanVariantLabel(getVariantName(v))),
                  );
                  const hasTree = enableVariants && meaningfulVariants.length > 1;
                  const isExpanded = !!expandedProducts[prod.id];

                  const mainRow = (
                    <tr
                      key={`desktop-${prod.id}`}
                      onClick={() => openDetalle(prod.id)}
                      className="hover:bg-slate-50/60 transition-all cursor-pointer group"
                      title="Abrir detalle"
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 shadow-sm flex items-center justify-center shrink-0 overflow-hidden">
                            {safeImageUrl ? (
                              <img src={safeImageUrl} alt={prod.name} className="w-full h-full object-cover" />
                            ) : (
                              <Package size={16} className="text-slate-300" />
                            )}
                          </div>

                          {hasTree ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleExpand(prod.id); }}
                              className="w-7 h-7 rounded-xl bg-slate-50 border border-slate-100 hover:bg-slate-100/60 transition-all flex items-center justify-center shrink-0"
                              title={isExpanded ? 'Ocultar variantes' : 'Ver variantes'}
                            >
                              {isExpanded ? (
                                <ChevronDown size={14} className="text-slate-500" />
                              ) : (
                                <ChevronRight size={14} className="text-slate-500" />
                              )}
                            </button>
                          ) : null}

                          <div className="min-w-0">
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="text-[11px] font-black uppercase text-slate-800 truncate group-hover:text-[#51A7FD] transition-colors">
                                {prod.name}
                              </div>
                              {prod.stock_tracking === 'variant' && (
                                <span className="text-[9px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-xl whitespace-nowrap">
                                  Talles
                                </span>
                              )}
                              {hasTree ? (
                                <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-xl whitespace-nowrap">
                                  {meaningfulVariants.length} var.
                                </span>
                              ) : null}
                            </div>
                            <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 truncate">
                              {catName}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-4">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                          {catName}
                        </span>
                      </td>

                      <td className="px-4 py-4">
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                          {brandName || 'Sin marca'}
                        </span>
                      </td>

                      <td className="px-4 py-4">
                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-slate-50 border border-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-500">
                          <Barcode size={12} className="text-slate-300" />
                          {prod.barcode || 'SIN EAN'}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest border ${stockTone}`}>
                          {stock}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${cost > 0 ? 'text-slate-700' : 'text-amber-600'}`}>
                          {cost > 0 ? fmtMoney(cost) : 'SIN COSTO'}
                        </span>
                      </td>

                      <td className="px-4 py-4 text-right">
                        <span className={`text-[10px] font-black uppercase tracking-widest ${price > 0 ? 'text-slate-700' : 'text-rose-600'}`}>
                          {price > 0 ? fmtMoney(price) : 'SIN PRECIO'}
                        </span>
                      </td>

                      <td className="px-5 py-4 text-right">
                        <ChevronRight
                          size={16}
                          className="inline text-slate-200 group-hover:text-[#51A7FD] group-hover:translate-x-1 transition-all"
                        />
                      </td>
                    </tr>
                  );

                  const childRows = (hasTree && isExpanded)
                    ? meaningfulVariants.map((v) => {
                        const vName = getCleanVariantLabel(getVariantName(v)) || 'Variante';
                        const vPrice = Number(v?.price || 0);
                        const vDesc = String(v?.attributes?.variant_description || '').trim();
                        const vActive = v?.attributes?.variant_active !== false;
                        return (
                          <tr
                            key={`desktop-${prod.id}-${v.id}`}
                            onClick={() => openDetalle(prod.id)}
                            className="bg-slate-50/30 hover:bg-blue-50/20 transition-all cursor-pointer"
                            title="Abrir detalle"
                          >
                            <td className="px-5 py-2.5">
                              <div className="flex items-center gap-2 pl-10">
                                <div className="pl-3 border-l-2 border-slate-200">
                                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-700">
                                    {vName}
                                    {!vActive && (
                                      <span className="ml-2 text-[8px] font-black text-rose-500">INACTIVA</span>
                                    )}
                                  </div>
                                  {vDesc && (
                                    <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 truncate max-w-[260px]">
                                      {vDesc}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-2.5" />
                            <td className="px-4 py-2.5" />
                            <td className="px-4 py-2.5">
                              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-white border border-slate-100 text-[9px] font-black uppercase tracking-widest text-slate-500">
                                <Barcode size={12} className="text-slate-300" />
                                {v?.barcode || v?.sku || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-center">
                              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">—</span>
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">—</span>
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <span className={`text-[10px] font-black uppercase tracking-widest ${vPrice > 0 ? 'text-slate-700' : 'text-rose-600'}`}>
                                {vPrice > 0 ? fmtMoney(vPrice) : 'SIN PRECIO'}
                              </span>
                            </td>
                            <td className="px-5 py-2.5" />
                          </tr>
                        );
                      })
                    : null;

                  return (
                    <React.Fragment key={`desktop-wrap-${prod.id}`}>
                      {mainRow}
                      {childRows}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        select {
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.75rem center;
          background-size: 0.75rem;
        }
        .nova-check-pop { animation: novaCheckPop 0.65s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaCheckPop {
          0% { transform: scale(0.6); opacity: 0; }
          35% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default Productos;

